import { useRouter } from 'next/router'
import { useGqlExamTestsQuery } from 'generated/graphql'
import { formatToCustomDate } from '@app.feature/exam/modules'
import { useCurrentUserContext } from 'hooks/useCurrentUser'

/**
 * @description 계정이 관리자인지, 일반 사용자인지 구분하여 각 사용자가 확인할 수 있는 정보를 반환합니다
 *
 * **각 사용자가 확인할 수 있는 정보**
 * - 관리자: 모든 시험 기간을 선택하여 확인할 수 있습니다 (기본값은 해당 시험 기간의 날짜로 설정됩니다)
 *
 * - 일반 사용자: 자신의 시험 기간에 맞는 키워드 문항 정보만 확인할 수 있습니다
 *
 * @returns
 */

type TUseExamQuestionInfoParams = {
  companyId: string
  termId: string
}

export const useExamQuestionInfo = ({
  companyId,
  termId,
}: TUseExamQuestionInfoParams) => {
  const { query } = useRouter()
  const { user } = useCurrentUserContext()
  const { data } = useGqlExamTestsQuery({
    variables: {
      input: {
        companyId,
        termId,
      },
    },
  })

  const testDate = data?.examTests.find((item) => {
    if (query.adminTestId) {
      return query.adminTestId === item.id
    } else {
      return query.testId === item.id
    }
  })

  const examDateList = data?.examTests?.map((test) => ({
    label: formatToCustomDate(test.date),
    value: test.id,
  }))

  /**
   * @description 현재 날짜와 시간을 기준으로 기본 시험을 결정하는 함수
   *
   * 1. 현재 날짜가 모든 시험보다 이전인 경우: 가장 이른 시험 날짜를 반환
   * 2. 현재 날짜가 일부 시험 이후인 경우: 현재 날짜 이전의 가장 최근 시험을 반환
   *    예) 시험일이 3/21 오전 9시, 4/1 오후 10시, 4/30 오전 9시인 경우:
   *        - 3/21 오전 9시 이후부터 4/1 오후 9시 59분까지는 3/21 오전 9시 시험을 반환
   *        - 4/1 오후 10시부터 4/30 오전 8시 59분까지는 4/1 오후 10시 시험을 반환
   * 3. 현재 날짜가 모든 시험보다 이후인 경우: 마지막 시험 날짜를 반환
   */
  const getDefaultTest = () => {
    const nowDate = new Date()

    if (!data?.examTests || data.examTests.length === 0) {
      return null
    }

    const sortedTests = [...data.examTests].sort(
      (a, b) => Number(a.date) - Number(b.date),
    )

    const futureTests = sortedTests.filter(
      (test) => Number(test.date) > nowDate.getTime(),
    )

    const pastTests = sortedTests.filter(
      (test) => Number(test.date) <= nowDate.getTime(),
    )

    if (futureTests.length === sortedTests.length) {
      return sortedTests[0]
    }

    if (pastTests.length === sortedTests.length) {
      return sortedTests[sortedTests.length - 1]
    }

    if (pastTests.length > 0) {
      return pastTests[pastTests.length - 1]
    }

    return futureTests[0]
  }

  const defaultTest = getDefaultTest()

  const isAdmin = user?.isSuperuser
  const date = testDate?.date
  const examDate = date ? date : defaultTest?.date

  return {
    isAdmin,
    examDate,
    testDate,
    examDateList,
    defaultTest,
  }
}
